import React, { useState, useMemo, useCallback } from 'react';
import {
  getForecastMonths15,
  getPriorMonth,
  mockSalesPlanBulkEditData,
  SalesPlanBulkEditMockRow,
  SUPPLIER_OPTIONS,
  CONTRACT_TYPE_OPTIONS,
  COT_OPTIONS,
  SELLING_CATEGORY_OPTIONS,
  RAMPING_OPTIONS,
  STRATEGY_CODE_OPTIONS,
} from './salesPlanBulkEditMockData';

/* ------------------------------------------------------------------ */
/*  Inline CSS-module-like styles (plain objects, no PrimeReact)       */
/* ------------------------------------------------------------------ */

const colors = {
  primary: '#4d2e6d',
  primaryLight: '#7B4B9F',
  white: '#ffffff',
  grey: '#e5e7eb',
  greyLight: '#f3f4f6',
  greyDark: '#6b7280',
  teal: '#028383',
  red: '#d8000c',
  green: '#059669',
  greenBg: '#d1fae5',
  redBg: '#fee2e2',
  expandedBg: '#eef2ff',
  rowAlt: '#F7F3FE',
  bannerBg: '#fef3c7',
  bannerBorder: '#f59e0b',
  priorCycleBg: '#eef2ff',
};

const s = {
  /* ---- layout ---- */
  container: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    fontSize: '13px',
    color: '#1f2937',
  } as React.CSSProperties,

  /* ---- info banner ---- */
  banner: {
    margin: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: colors.bannerBg,
    border: `1px solid ${colors.bannerBorder}`,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '13px',
  } as React.CSSProperties,
  badge: {
    backgroundColor: colors.bannerBorder,
    color: colors.white,
    padding: '2px 8px',
    borderRadius: '4px',
    fontWeight: 700,
    fontSize: '11px',
    letterSpacing: '0.5px',
  } as React.CSSProperties,

  /* ---- filter section ---- */
  filterContainer: {
    border: `1px solid ${colors.grey}`,
    borderRadius: '8px',
    backgroundColor: colors.white,
    margin: '1rem',
    padding: '1rem',
  } as React.CSSProperties,
  filterRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '1rem',
    marginBottom: '0.75rem',
  } as React.CSSProperties,
  filterField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  } as React.CSSProperties,
  filterLabel: {
    fontWeight: 500,
    fontSize: '13px',
    color: '#374151',
  } as React.CSSProperties,
  filterInput: {
    height: '2rem',
    borderRadius: '4px',
    border: `1px solid ${colors.grey}`,
    padding: '0 0.5rem',
    fontSize: '13px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  } as React.CSSProperties,
  filterSelect: {
    height: '2rem',
    borderRadius: '4px',
    border: `1px solid ${colors.grey}`,
    padding: '0 0.5rem',
    fontSize: '13px',
    backgroundColor: colors.white,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  } as React.CSSProperties,
  filterBtnRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.75rem',
    marginTop: '0.5rem',
  } as React.CSSProperties,

  /* ---- grid section ---- */
  gridWrapper: {
    border: `1px solid ${colors.grey}`,
    borderRadius: '8px',
    backgroundColor: colors.white,
    margin: '1rem',
    height: '64vh',
    overflowY: 'auto',
    overflowX: 'auto',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    margin: '-1px',
  } as React.CSSProperties,
  thead: {
    backgroundColor: colors.primaryLight,
    position: 'sticky',
    top: 0,
    zIndex: 10,
  } as React.CSSProperties,
  th: {
    color: colors.white,
    fontWeight: 500,
    fontSize: '12px',
    lineHeight: '100%',
    padding: '0.5rem 0.35rem',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  td: {
    textAlign: 'center',
    padding: '0.35rem',
    fontSize: '12px',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,

  /* ---- buttons ---- */
  btn: {
    height: '2rem',
    padding: '0 1rem',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '13px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
  } as React.CSSProperties,
  primaryBtn: {
    backgroundColor: colors.primaryLight,
    color: colors.white,
  } as React.CSSProperties,
  clearBtn: {
    backgroundColor: colors.rowAlt,
    color: colors.primary,
    border: `1px solid ${colors.greyDark}`,
  } as React.CSSProperties,
  tealBtn: {
    backgroundColor: colors.teal,
    color: colors.white,
  } as React.CSSProperties,
  redBtn: {
    backgroundColor: colors.red,
    color: colors.white,
  } as React.CSSProperties,
  smallBtn: {
    height: '1.6rem',
    padding: '0 0.6rem',
    fontSize: '11px',
    borderRadius: '3px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.3rem',
  } as React.CSSProperties,

  /* ---- cell input ---- */
  cellInput: {
    width: '5.5rem',
    fontSize: '12px',
    height: '1.4rem',
    border: `1px solid ${colors.grey}`,
    borderRadius: '3px',
    textAlign: 'right',
    padding: '0 0.3rem',
    outline: 'none',
  } as React.CSSProperties,

  /* ---- copy dot ---- */
  copyDot: {
    position: 'absolute',
    bottom: '4px',
    right: '6px',
    cursor: 'pointer',
    fontSize: '22px',
    lineHeight: '10px',
    color: colors.primaryLight,
    fontWeight: 'bold',
    userSelect: 'none',
  } as React.CSSProperties,

  /* ---- comment popover ---- */
  commentPopover: {
    position: 'absolute',
    right: '1rem',
    marginTop: '0.25rem',
    zIndex: 20,
    backgroundColor: colors.white,
    border: `1px solid ${colors.grey}`,
    borderRadius: '6px',
    padding: '0.75rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
    width: '220px',
  } as React.CSSProperties,

  /* ---- prior cycle row ---- */
  priorLabel: {
    padding: '0.4rem 0.5rem',
    fontSize: '11px',
    fontWeight: 700,
    color: colors.primaryLight,
  } as React.CSSProperties,
  priorValue: {
    padding: '0.4rem 0.3rem',
    fontSize: '11px',
    textAlign: 'center',
    fontWeight: 600,
    color: colors.primaryLight,
  } as React.CSSProperties,

  /* ---- bottom bar ---- */
  bottomBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '0 1rem 1rem',
  } as React.CSSProperties,
} as const;

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Filters {
  ramping: string;
  molecule: string;
  sellingCategory: string;
  supplierName: string;
  concept: string;
  strategyCode: string;
  gfc: string;
  contractType: string;
  cot: string;
}

const emptyFilters: Filters = {
  ramping: '',
  molecule: '',
  sellingCategory: '',
  supplierName: '',
  concept: '',
  strategyCode: '',
  gfc: '',
  contractType: '',
  cot: '',
};

/* ------------------------------------------------------------------ */
/*  Helper: unique row key from GFC + COT + SellingCategory            */
/* ------------------------------------------------------------------ */
const getRowKey = (row: SalesPlanBulkEditMockRow): string =>
  `${row.GFC}-${row.COT}-${row.SellingCategory}`;

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const SalesPlanBulkEditUpdated: React.FC = () => {
  /* ---- static data ---- */
  const forecastMonths = useMemo(() => getForecastMonths15(), []);
  const priorMonth = useMemo(() => getPriorMonth(), []);

  /* ---- state ---- */
  const [filters, setFilters] = useState<Filters>({ ...emptyFilters });
  const [appliedFilters, setAppliedFilters] = useState<Filters>({ ...emptyFilters });
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [editValues, setEditValues] = useState<Map<string, Map<string, string>>>(() => {
    const map = new Map<string, Map<string, string>>();
    mockSalesPlanBulkEditData.forEach(row => {
      const key = getRowKey(row);
      const rowMap = new Map<string, string>();
      row.forecast_data.forEach(fd => {
        rowMap.set(fd.ForecastPeriod, String(fd.EUSalesPlan));
      });
      map.set(key, rowMap);
    });
    return map;
  });
  const [conceptSort, setConceptSort] = useState<'' | 'asc' | 'desc'>('');
  const [focusedCell, setFocusedCell] = useState<string | null>(null);
  const [commentOpen, setCommentOpen] = useState<number | null>(null);
  const [commentText, setCommentText] = useState<string>('');

  /* ---- derived: filtered + sorted data ---- */
  const filteredData: SalesPlanBulkEditMockRow[] = useMemo(() => {
    let result = [...mockSalesPlanBulkEditData];

    const f = appliedFilters;

    // Text filters (case-insensitive substring)
    if (f.concept) {
      const lc = f.concept.toLowerCase();
      result = result.filter(r => r.Concept.toLowerCase().includes(lc));
    }
    if (f.gfc) {
      const lc = f.gfc.toLowerCase();
      result = result.filter(r => String(r.GFC).toLowerCase().includes(lc));
    }
    if (f.molecule) {
      const lc = f.molecule.toLowerCase();
      result = result.filter(r => r.MoleculeAndDosageForm.toLowerCase().includes(lc));
    }

    // Dropdown exact match
    if (f.ramping) {
      const boolVal = f.ramping === 'true';
      result = result.filter(r => r.Ramping === boolVal);
    }
    if (f.sellingCategory) {
      result = result.filter(r => r.SellingCategory === f.sellingCategory);
    }
    if (f.strategyCode) {
      result = result.filter(r => r.StrategyCode === f.strategyCode);
    }
    if (f.supplierName) {
      result = result.filter(r => r.SupplierName === f.supplierName);
    }
    if (f.contractType) {
      result = result.filter(r => r.ContractType === f.contractType);
    }
    if (f.cot) {
      result = result.filter(r => r.COT === f.cot);
    }

    // Sorting
    if (conceptSort === 'asc') {
      result.sort((a, b) => a.Concept.localeCompare(b.Concept));
    } else if (conceptSort === 'desc') {
      result.sort((a, b) => b.Concept.localeCompare(a.Concept));
    }

    return result;
  }, [appliedFilters, conceptSort]);

  /* ---- handlers ---- */
  const handleFilterChange = useCallback(
    (field: keyof Filters, value: string) => {
      setFilters(prev => ({ ...prev, [field]: value }));
    },
    [],
  );

  const handleApply = useCallback(() => {
    setAppliedFilters({ ...filters });
    setExpandedRows(new Set());
  }, [filters]);

  const handleClear = useCallback(() => {
    setFilters({ ...emptyFilters });
    setAppliedFilters({ ...emptyFilters });
    setExpandedRows(new Set());
  }, []);

  const toggleExpand = useCallback((idx: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const cycleSort = useCallback(() => {
    setConceptSort(prev => {
      if (prev === '') return 'asc';
      if (prev === 'asc') return 'desc';
      return '';
    });
  }, []);

  const getSortArrow = (): string => {
    if (conceptSort === 'asc') return ' \u25B2';
    if (conceptSort === 'desc') return ' \u25BC';
    return ' \u25C6';
  };

  const handleCellChange = useCallback(
    (rowKey: string, dateStr: string, value: string) => {
      const stripped = value.replace(/,/g, '');
      setEditValues(prev => {
        const next = new Map(prev);
        const rowMap = new Map(next.get(rowKey) || new Map());
        rowMap.set(dateStr, stripped);
        next.set(rowKey, rowMap);
        return next;
      });
    },
    [],
  );

  const getCellValue = (rowKey: string, dateStr: string): string => {
    const raw = editValues.get(rowKey)?.get(dateStr) ?? '';
    if (raw === '') return '';
    const num = parseFloat(raw.replace(/,/g, ''));
    if (isNaN(num)) return raw;
    return num.toLocaleString('en-US');
  };

  const handleCopyForward = useCallback(
    (rowKey: string, fromDateStr: string) => {
      const value = getCellValue(rowKey, fromDateStr);
      const startIdx = forecastMonths.findIndex(m => m.dateString === fromDateStr);
      if (startIdx < 0) return;
      setEditValues(prev => {
        const next = new Map(prev);
        const rowMap = new Map(next.get(rowKey) || new Map());
        for (let i = startIdx + 1; i < forecastMonths.length; i++) {
          rowMap.set(forecastMonths[i].dateString, value);
        }
        next.set(rowKey, rowMap);
        return next;
      });
      setFocusedCell(null);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [forecastMonths, editValues],
  );

  const handleSaveRow = useCallback((rowKey: string) => {
    alert(`[MOCKUP] Save triggered for row ${rowKey}`);
  }, []);

  const handleSaveAll = useCallback(() => {
    alert('[MOCKUP] Save All triggered');
  }, []);

  const handleComment = useCallback((idx: number) => {
    setCommentOpen(prev => (prev === idx ? null : idx));
    setCommentText('');
  }, []);

  const handleCommentSubmit = useCallback(
    (rowKey: string) => {
      alert(`[MOCKUP] Comment for ${rowKey}: "${commentText}"`);
      setCommentOpen(null);
      setCommentText('');
    },
    [commentText],
  );

  /* Total columns: expand(1) + Concept + GFC + COT + SellingCategory + Type (= 6 fixed) + 15 months + Actions = 22 */
  const fixedColCount = 6;
  const totalColSpan = fixedColCount + forecastMonths.length + 1;

  /* ================================================================ */
  /*  RENDER                                                           */
  /* ================================================================ */

  return (
    <div style={s.container}>
      {/* ---- Info Banner ---- */}
      <div style={s.banner}>
        <span style={s.badge}>MOCKUP</span>
        <span>
          Sales Plan Bulk Edit (15-Month). Features: Supplier &amp; Contract Type filters,
          Prior cycle visibility, 15-month forecast.
        </span>
      </div>

      {/* ================================================================ */}
      {/*  FILTER SECTION                                                   */}
      {/* ================================================================ */}
      <div style={s.filterContainer}>
        {/* Row 1 */}
        <div style={s.filterRow}>
          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Ramping:</label>
            <select
              style={s.filterSelect}
              value={filters.ramping}
              onChange={e => handleFilterChange('ramping', e.target.value)}
            >
              {RAMPING_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Molecule:</label>
            <input
              type="text"
              placeholder="Search molecule..."
              style={s.filterInput}
              value={filters.molecule}
              onChange={e => handleFilterChange('molecule', e.target.value)}
            />
          </div>

          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Selling Category:</label>
            <select
              style={s.filterSelect}
              value={filters.sellingCategory}
              onChange={e => handleFilterChange('sellingCategory', e.target.value)}
            >
              {SELLING_CATEGORY_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Supplier Name:</label>
            <select
              style={s.filterSelect}
              value={filters.supplierName}
              onChange={e => handleFilterChange('supplierName', e.target.value)}
            >
              {SUPPLIER_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 2 */}
        <div style={s.filterRow}>
          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Concept:</label>
            <input
              type="text"
              placeholder="Search concept..."
              style={s.filterInput}
              value={filters.concept}
              onChange={e => handleFilterChange('concept', e.target.value)}
            />
          </div>

          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Strategy Code:</label>
            <select
              style={s.filterSelect}
              value={filters.strategyCode}
              onChange={e => handleFilterChange('strategyCode', e.target.value)}
            >
              {STRATEGY_CODE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>NDC/GFC:</label>
            <input
              type="text"
              placeholder="Search GFC..."
              style={s.filterInput}
              value={filters.gfc}
              onChange={e => handleFilterChange('gfc', e.target.value)}
            />
          </div>

          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>Contract Type:</label>
            <select
              style={s.filterSelect}
              value={filters.contractType}
              onChange={e => handleFilterChange('contractType', e.target.value)}
            >
              {CONTRACT_TYPE_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Row 3: COT filter */}
        <div style={s.filterRow}>
          <div style={s.filterField as React.CSSProperties}>
            <label style={s.filterLabel}>COT:</label>
            <select
              style={s.filterSelect}
              value={filters.cot}
              onChange={e => handleFilterChange('cot', e.target.value)}
            >
              {COT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {/* Empty placeholders to maintain grid alignment */}
          <div />
          <div />
          <div />
        </div>

        {/* Button row */}
        <div style={s.filterBtnRow}>
          <button
            style={{ ...s.btn, ...s.clearBtn }}
            onClick={handleClear}
          >
            Clear Filter
          </button>
          <button
            style={{ ...s.btn, ...s.primaryBtn }}
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>

      {/* ================================================================ */}
      {/*  GRID SECTION                                                     */}
      {/* ================================================================ */}
      <div style={s.gridWrapper}>
        <table style={s.table}>
          <thead style={s.thead}>
            <tr>
              {/* Expand */}
              <th style={{ ...s.th, width: '2.5rem', borderTopLeftRadius: '8px' }}></th>
              {/* Concept - sortable */}
              <th
                style={{ ...s.th, width: '11rem', cursor: 'pointer', userSelect: 'none' }}
                onClick={cycleSort}
                title="Click to sort by Concept"
              >
                Concept{getSortArrow()}
              </th>
              {/* GFC */}
              <th style={{ ...s.th, width: '5rem' }}>GFC</th>
              {/* COT */}
              <th style={{ ...s.th, width: '6rem' }}>COT</th>
              {/* Selling Category */}
              <th style={{ ...s.th, width: '6.5rem' }}>Selling Category</th>
              {/* Type */}
              <th style={{ ...s.th, width: '8rem' }}>Type</th>
              {/* 15 month columns */}
              {forecastMonths.map(m => (
                <th key={m.dateString} style={{ ...s.th, minWidth: '6rem' }}>
                  {m.label}
                </th>
              ))}
              {/* Actions / Save All */}
              <th
                style={{
                  ...s.th,
                  width: '12rem',
                  backgroundColor: colors.white,
                  borderTopRightRadius: '8px',
                  padding: '0.35rem 0.5rem',
                }}
              >
                <button
                  style={{ ...s.btn, ...s.tealBtn, width: '100%' }}
                  onClick={handleSaveAll}
                >
                  &#10003; Save All
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 && (
              <tr>
                <td
                  colSpan={totalColSpan}
                  style={{ padding: '3rem', textAlign: 'center', color: colors.greyDark }}
                >
                  No Sales Plan data available for the applied filter values.
                </td>
              </tr>
            )}

            {filteredData.map((row, idx) => {
              const isExpanded = expandedRows.has(idx);
              const bgColor = idx % 2 ? colors.rowAlt : colors.white;
              const rowKey = getRowKey(row);

              return (
                <React.Fragment key={`${rowKey}-${idx}`}>
                  {/* ---- Main Row ---- */}
                  <tr style={{ backgroundColor: bgColor, height: '3.5rem' }}>
                    {/* Expand button */}
                    <td style={{ ...s.td, width: '2.5rem' }}>
                      <span
                        style={{ cursor: 'pointer', fontSize: '14px', userSelect: 'none' }}
                        onClick={() => toggleExpand(idx)}
                        title={isExpanded ? 'Collapse' : 'Expand to see prior cycle'}
                      >
                        {isExpanded ? '\u25BC' : '\u25B6'}
                      </span>
                    </td>
                    {/* Concept */}
                    <td style={{ ...s.td, textAlign: 'left', paddingLeft: '0.5rem', fontSize: '11px' }}>
                      {row.Concept}
                    </td>
                    {/* GFC */}
                    <td style={{ ...s.td, fontSize: '11px' }}>{row.GFC}</td>
                    {/* COT */}
                    <td style={{ ...s.td, fontSize: '11px' }}>{row.COT.toUpperCase()}</td>
                    {/* Selling Category */}
                    <td style={{ ...s.td, fontSize: '11px' }}>
                      {row.SellingCategory.charAt(0).toUpperCase() + row.SellingCategory.slice(1)}
                    </td>
                    {/* Type */}
                    <td style={{ ...s.td, width: '8rem' }}>
                      <span style={{ fontSize: '11px', color: colors.greyDark }}>Current</span>
                    </td>
                    {/* 15 month cells - EU Sales Plan (editable) */}
                    {forecastMonths.map((m, mIdx) => {
                      const cellKey = `${rowKey}-${idx}-${m.dateString}`;
                      const isFocused = focusedCell === cellKey;
                      return (
                        <td key={m.dateString} style={{ ...s.td, position: 'relative' }}>
                          <input
                            type="text"
                            style={s.cellInput as React.CSSProperties}
                            value={getCellValue(rowKey, m.dateString)}
                            onFocus={() => setFocusedCell(cellKey)}
                            onBlur={() => {
                              // Delay blur to allow copy-dot click
                              setTimeout(() => {
                                setFocusedCell(prev => (prev === cellKey ? null : prev));
                              }, 150);
                            }}
                            onChange={e => handleCellChange(rowKey, m.dateString, e.target.value)}
                          />
                          {isFocused && mIdx < forecastMonths.length - 1 && (
                            <span
                              title="Copy value to all cells to the right"
                              style={s.copyDot as React.CSSProperties}
                              onMouseDown={e => {
                                e.preventDefault();
                                handleCopyForward(rowKey, m.dateString);
                              }}
                            >
                              &#9679;
                            </span>
                          )}
                        </td>
                      );
                    })}
                    {/* Actions */}
                    <td style={{ ...s.td, width: '12rem', position: 'relative' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', justifyContent: 'center' }}>
                        <button
                          style={{ ...s.smallBtn, backgroundColor: colors.teal, color: colors.white }}
                          onClick={() => handleSaveRow(rowKey)}
                        >
                          &#128190; Save
                        </button>
                        <button
                          style={{ ...s.smallBtn, backgroundColor: colors.primaryLight, color: colors.white }}
                          onClick={() => handleComment(idx)}
                        >
                          &#128172;
                        </button>
                      </div>
                      {/* Inline comment popover */}
                      {commentOpen === idx && (
                        <div style={s.commentPopover as React.CSSProperties}>
                          <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '0.4rem' }}>
                            Add Comment (GFC: {row.GFC}, COT: {row.COT})
                          </div>
                          <textarea
                            rows={3}
                            style={{
                              width: '100%',
                              fontSize: '12px',
                              border: `1px solid ${colors.grey}`,
                              borderRadius: '4px',
                              padding: '0.3rem',
                              resize: 'none',
                              boxSizing: 'border-box',
                            }}
                            value={commentText}
                            onChange={e => setCommentText(e.target.value)}
                            placeholder="Enter comment..."
                          />
                          <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.4rem', justifyContent: 'flex-end' }}>
                            <button
                              style={{ ...s.smallBtn, ...s.clearBtn, fontSize: '10px' }}
                              onClick={() => setCommentOpen(null)}
                            >
                              Cancel
                            </button>
                            <button
                              style={{ ...s.smallBtn, backgroundColor: colors.teal, color: colors.white, fontSize: '10px' }}
                              onClick={() => handleCommentSubmit(rowKey)}
                            >
                              Save
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* ---- Expanded Row: Prior Cycle Forecast ---- */}
                  {isExpanded && (
                    <tr style={{ backgroundColor: colors.expandedBg, borderTop: `1px solid ${colors.grey}` }}>
                      <td style={s.td}></td>
                      <td
                        colSpan={fixedColCount - 1}
                        style={s.priorLabel as React.CSSProperties}
                      >
                        Prior Cycle ({priorMonth.label})
                      </td>
                      {forecastMonths.map((_m, mIdx) => (
                        <td
                          key={`prior-${_m.dateString}`}
                          style={s.priorValue as React.CSSProperties}
                        >
                          {row.priorCycleForecast && row.priorCycleForecast[mIdx] != null
                            ? row.priorCycleForecast[mIdx].toLocaleString()
                            : '\u2014'}
                        </td>
                      ))}
                      <td></td>
                    </tr>
                  )}

                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ---- Bottom Bar ---- */}
      <div style={s.bottomBar}>
        <button
          style={{ ...s.btn, ...s.redBtn }}
          onClick={() => alert('[MOCKUP] Cancel clicked')}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default SalesPlanBulkEditUpdated;
